/**
 * Rows a run wrote into the conversation through the operator: a node's
 * clarifying question, how it ended, and later a draft for review or the run
 * finishing. Written by the backend relay; read back here off the row's
 * metadata so the transcript can badge them and the composer can tell what
 * is waiting.
 */

import type { AgentChatArtifact } from '@/services/agents/chat-stream';

export const RUN_EVENT_KINDS = [
  'node_clarification',
  'node_review',
  'node_review_reply',
  'node_outcome',
  'run_completed',
  'run_failed',
  'run_stopped',
] as const;

export type RunEventKind = (typeof RUN_EVENT_KINDS)[number];

export interface RelayedRunEvent {
  kind: RunEventKind;
  runId?: string;
  sessionId?: string;
  workflowId?: string;
  workflowName?: string;
  nodeId?: string;
  nodeTitle?: string;
  requestKey?: string;
  question?: string;
  context?: string;
  deadlineEpochMs?: number;
  outcome?: string;
  /** Review: where a document node's draft can be opened. */
  reviewUrl?: string;
  round?: number;
  /** Run result: how the run ended and what it produced. */
  terminalStatus?: string;
  durationSecs?: number;
  error?: string;
  /** Run result: what the run produced, in the shape the chat cards render. */
  artifacts?: AgentChatArtifact[];
}

function isKind(value: unknown): value is RunEventKind {
  return (
    typeof value === 'string' && RUN_EVENT_KINDS.includes(value as RunEventKind)
  );
}

function optionalString(value: unknown): string | undefined {
  return typeof value === 'string' && value ? value : undefined;
}

// DynamoDB numbers come back through the messages endpoint as strings.
function optionalNumber(value: unknown): number | undefined {
  if (typeof value === 'number')
    return Number.isFinite(value) ? value : undefined;
  if (typeof value === 'string' && value.trim()) {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : undefined;
  }
  return undefined;
}

/**
 * The run event a persisted row carries, or undefined for an ordinary turn.
 * An unrecognized payload reads as nothing, so a row written by a newer
 * backend does not break an older client's transcript.
 */
export function runEventFromMetadata(message: {
  metadata?: Record<string, unknown>;
}): RelayedRunEvent | undefined {
  const kind = message.metadata?.kind;
  if (!isKind(kind)) return undefined;
  const raw = message.metadata?.run_event;
  const event =
    raw && typeof raw === 'object' ? (raw as Record<string, unknown>) : {};
  return {
    kind,
    runId: optionalString(event.run_id),
    sessionId: optionalString(event.session_id),
    workflowId: optionalString(event.workflow_id),
    workflowName: optionalString(event.workflow_name),
    nodeId: optionalString(event.node_id),
    nodeTitle: optionalString(event.node_title),
    requestKey:
      optionalString(event.request_key) ??
      optionalString(message.metadata?.request_key),
    question: optionalString(event.question),
    context: optionalString(event.context),
    deadlineEpochMs: optionalNumber(event.deadline_epoch_ms),
    outcome: optionalString(event.outcome),
    reviewUrl: optionalString(event.review_url),
    round: optionalNumber(event.round),
    terminalStatus: optionalString(event.terminal_status),
    durationSecs: optionalNumber(event.duration_secs),
    error: optionalString(event.error),
    artifacts: Array.isArray(event.artifacts)
      ? (event.artifacts as AgentChatArtifact[])
      : undefined,
  };
}

const RUN_ENDED_KINDS = new Set(['run_completed', 'run_failed', 'run_stopped']);

/**
 * The input of one kind still waiting on the user in this transcript: the
 * newest row of that kind with no outcome row for the same request after
 * it, on a run that has not ended since. The backend closes every ask with
 * an outcome, so a row with none is one the run is still parked on, even
 * when a later ask has already closed; a run that ended after asking is
 * not parked on anything, whether or not its outcome row landed.
 */
function pendingInputIn<T extends { _runEvent?: RelayedRunEvent }>(
  messages: T[],
  kind: 'node_clarification' | 'node_review',
): RelayedRunEvent | null {
  const closed = new Set<string>();
  const endedRuns = new Set<string>();
  for (let i = messages.length - 1; i >= 0; i -= 1) {
    const event = messages[i]._runEvent;
    if (!event) continue;
    if (RUN_ENDED_KINDS.has(event.kind) && event.runId) {
      endedRuns.add(event.runId);
      continue;
    }
    if (!event.requestKey) continue;
    if (event.kind === 'node_outcome') {
      closed.add(event.requestKey);
      continue;
    }
    if (
      event.kind === kind &&
      !closed.has(event.requestKey) &&
      !(event.runId && endedRuns.has(event.runId))
    ) {
      return event;
    }
  }
  return null;
}

export function pendingClarificationIn<
  T extends { _runEvent?: RelayedRunEvent },
>(messages: T[]): RelayedRunEvent | null {
  return pendingInputIn(messages, 'node_clarification');
}

export function pendingReviewIn<T extends { _runEvent?: RelayedRunEvent }>(
  messages: T[],
): RelayedRunEvent | null {
  return pendingInputIn(messages, 'node_review');
}

export function describeOutcome(outcome: string | undefined): string | null {
  switch (outcome) {
    case 'answered':
      return 'Answered';
    case 'answered_elsewhere':
      return 'Answered from another chat';
    case 'skipped':
      return 'Skipped';
    case 'timed_out':
      return 'No answer in time';
    case 'abandoned':
      return 'Run stopped';
    case 'approved':
      return 'Approved';
    case 'run_stopped':
      return 'Run stopped';
    default:
      return null;
  }
}
